import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface RankRule {
  rank: string;
  title: string;
  body: string;
}

const RANK_RULES: RankRule[] = [
  { rank: '2', title: 'Pick up two', body: 'The next player takes two cards — unless they lay a two of their own and pass a bigger pile along.' },
  { rank: '7', title: 'Skip, or mirror', body: 'Depending on the table, a seven either misses the next player out or forces them to follow its suit.' },
  { rank: '8', title: 'Another go', body: 'Take a second turn straight away. Some tables use eights to skip the next player instead.' },
  { rank: 'J', title: 'The wild card', body: 'Play a Jack on anything and name the suit. Other tables have Jacks reverse the play, or make the black ones sting for five.' },
  { rank: 'Q', title: 'Usually nothing', body: 'An ordinary card at most tables, but she can be set to skip the next player.' },
  { rank: 'A', title: 'The Ace of Hearts', body: 'Picks up five, and only the Ace of Diamonds blocks it. Elsewhere every Ace is the wild card instead.' },
];

export function HowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Get rid of every card in your hand. Last one still holding cards buys the next round.
        </p>
      </header>

      <section className="prose__section">
        <h2>A turn</h2>
        <p>
          Play a card that matches the top of the pile by <strong>suit</strong> or by <strong>rank</strong>.
          A three of clubs goes on any club, or on any other three. If you can lay several of the same rank
          at once, you may — a three of clubs, then a three of diamonds, then a three of spades.
        </p>
        <p>
          Nothing to play? Take a card from the deck and your turn is over. Some tables let you lay what you
          just drew; some let you keep drawing until something fits.
        </p>
      </section>

      <section className="prose__section">
        <h2>The cards that bite</h2>
        <p className="prose__note">
          Every table plays these differently, so they are all settings the host chooses before dealing.
          These are the defaults.
        </p>
        <ul className="rulelist">
          {RANK_RULES.map((rule) => (
            <li key={rule.rank} className="rulelist__item">
              <span className="rulelist__rank" aria-hidden="true">
                {rule.rank}
              </span>
              <div>
                <h3>{rule.title}</h3>
                <p>{rule.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="prose__section">
        <h2>Pick-ups stack</h2>
        <p>
          A pick-up can be passed along. An Ace of Hearts opens at five; lay a two of hearts on it and the
          next player faces seven; another two makes it nine, and so on until somebody takes the lot. To
          answer a pick-up your card has to match normally as well — which is why only a <em>heart</em> two
          goes on the Ace of Hearts.
        </p>
        <p>
          Blocking is narrow. The Ace of Diamonds stops the Ace of Hearts and a red Jack stops a black one.
          Neither gets you out of a stack of twos.
        </p>
      </section>

      <section className="prose__section">
        <h2>Last card</h2>
        <p>
          When you play the card that leaves you with one, you must call <strong>"last card"</strong>. Stay
          quiet and any opponent can catch you, and you pick up a penalty for the trouble. The call travels
          with the play, so it arms itself as soon as you are down to two — you can switch it off if you
          fancy your chances.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
